import React from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { stepConnectorClasses } from '@mui/material/StepConnector';
import {
  ShoppingCart as CartIcon,
  Payment as PaymentIcon,
  CheckCircle as CompleteIcon,
  RateReview as ReviewIcon,
} from '@mui/icons-material';

const CustomConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 22,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: 'var(--primary)',
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: 'var(--primary)',
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor: '#e0e0e0',
    borderRadius: 1,
    marginLeft: 8,
    marginRight: 8,
  },
}));

const CustomStepIconRoot = styled('div')(({ theme, ownerState }) => ({
  backgroundColor: '#e0e0e0',
  zIndex: 1,
  color: '#fff',
  width: 50,
  height: 50,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  ...(ownerState.active && {
    backgroundColor: 'var(--primary)',
    boxShadow: '0 4px 10px 0 color-mix(in srgb, var(--primary) 25%, transparent)',
  }),
  ...(ownerState.completed && {
    backgroundColor: 'var(--primary)',
  }),
}));

function CustomStepIcon(props) {
  const { active, completed, icon, className } = props;

  const icons = {
    1: <CartIcon />,
    2: <PaymentIcon />,
    3: <ReviewIcon />,
  };

  return (
    <CustomStepIconRoot ownerState={{ completed, active }} className={className}>
      {completed ? <CompleteIcon /> : icons[String(icon)]}
    </CustomStepIconRoot>
  );
}

const CheckoutStepper = ({
  activeStep,
  steps = ['Cart Summary', 'Payment Method', 'Review Order'],
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ width: '100%', mb: 4 }}>
      <Stepper
        activeStep={activeStep}
        alternativeLabel={false}
        orientation={isMobile ? 'vertical' : 'horizontal'}
        connector={<CustomConnector />}
      >
        {steps.map((label, index) => (
          <Step key={label}>
            <StepLabel
              StepIconComponent={CustomStepIcon}
              sx={{
                '& .MuiStepLabel-label': {
                  mt: 1,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  fontWeight: activeStep === index ? 600 : 400,
                  color: activeStep === index ? 'var(--primary)' : 
                         index < activeStep ? 'var(--primary)' : '#999',
                  '&.Mui-completed': {
                    color: 'var(--primary)',
                    fontWeight: 500,
                  },
                  '&.Mui-active': {
                    color: 'var(--primary)',
                    fontWeight: 600,
                  },
                },
              }}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Progress bar for mobile */}
      {isMobile && (
        <Box sx={{ mt: 2, px: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Step {activeStep + 1} of {steps.length}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {Math.round(((activeStep + 1) / steps.length) * 100)}% Complete
            </Typography>
          </Box>
          <Box
            sx={{
              height: 4,
              backgroundColor: '#e0e0e0',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                height: '100%',
                width: `${((activeStep + 1) / steps.length) * 100}%`,
                backgroundColor: 'var(--primary)',
                transition: 'width 0.3s ease',
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

// Mini version for mobile header
export const MiniCheckoutProgress = ({
  activeStep,
  totalSteps = 3,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 1,
        backgroundColor: '#f5f5f5',
        borderRadius: 2,
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 500 }}>
        Checkout Progress:
      </Typography>
      <Box sx={{ flex: 1, mx: 1 }}>
        <Box
          sx={{
            height: 3,
            backgroundColor: '#e0e0e0',
            borderRadius: 1.5,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              height: '100%',
              width: `${((activeStep + 1) / totalSteps) * 100}%`,
              backgroundColor: 'var(--primary)',
              transition: 'width 0.3s ease',
            }}
          />
        </Box>
      </Box>
      <Typography variant="caption" color="text.secondary">
        {activeStep + 1}/{totalSteps}
      </Typography>
    </Box>
  );
};

export default CheckoutStepper;